import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { HazardReport, IncidentReport, ListResponse } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { CheckIcon, ShieldIcon } from '../../components/icons'

type Kind = 'incidents' | 'hazards'

export default function SafetyTriage() {
  const [kind, setKind] = useState<Kind>('incidents')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Safety triage</h1>
          <p className="page-head__sub">
            Acknowledge what your team reports, then resolve it once dealt with.
          </p>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button size="sm" variant={kind === 'incidents' ? 'primary' : 'secondary'}
                onClick={() => setKind('incidents')}>Incidents</Button>
        <Button size="sm" variant={kind === 'hazards' ? 'primary' : 'secondary'}
                onClick={() => setKind('hazards')}>Hazards</Button>
      </div>

      {kind === 'incidents' ? <IncidentTriage /> : <HazardTriage />}
    </>
  )
}

/* Both lists behave identically apart from their field names, so the row
 * actions are shared and the differences passed in. */
function TriageTable<T extends { id?: string; status?: string; description?: string }>({
  title, emptyTitle, emptyBody, emptyIcon, endpoint, queryKey,
  columns, rowCells,
}: {
  title: string
  emptyTitle: string
  emptyBody: string
  emptyIcon: React.ReactNode
  endpoint: string
  queryKey: string
  columns: string[]
  rowCells: (item: T) => React.ReactNode
}) {
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', queryKey],
    queryFn: () => api.get<ListResponse<T>>(endpoint),
  })

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'ack' | 'resolve' }) =>
      api.post(`${endpoint}/${id}/${action}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['leader'] }) },
  })

  const items = data?.items ?? []

  return (
    <>
      {act.isError && <Alert tone="danger">{(act.error as Error).message}</Alert>}
      <Card>
        <CardHeader title={title}
                    sub={items.length ? `${items.length} report${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="72%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={emptyIcon} title={emptyTitle} body={emptyBody} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>{columns.map((c) => <th key={c}>{c}</th>)}<th /></tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const status = (item.status ?? '').toLowerCase()
                    const done = status === 'resolved' || status === 'closed'
                    const acked = status === 'acknowledged' || status === 'in_progress'
                    return (
                      <tr key={item.id ?? i}>
                        {rowCells(item)}
                        <td className="table__num">
                          {item.id && !done && (
                            <div className="row" style={{ justifyContent: 'flex-end' }}>
                              {!acked && (
                                <Button size="sm" variant="secondary" disabled={act.isPending}
                                        onClick={() => act.mutate({ id: item.id!, action: 'ack' })}>
                                  Acknowledge
                                </Button>
                              )}
                              <Button size="sm" disabled={act.isPending}
                                      onClick={() => act.mutate({ id: item.id!, action: 'resolve' })}>
                                Resolve
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function IncidentTriage() {
  return (
    <TriageTable<IncidentReport>
      title="Incident reports"
      emptyTitle="No incidents reported"
      emptyBody="Incidents raised by your team will appear here for triage."
      emptyIcon={<CheckIcon size={22} />}
      endpoint="/leader/incidents"
      queryKey="incidents"
      columns={['Type', 'Severity', 'Occurred', 'Description', 'Status']}
      rowCells={(r) => (
        <>
          <td>{titleCase(r.incident_type)}</td>
          <td><StatusBadge status={r.severity} /></td>
          <td>{formatDate(r.occurred_at)}</td>
          <td style={{ maxWidth: 300 }}>{r.description || '—'}</td>
          <td><StatusBadge status={r.status} /></td>
        </>
      )}
    />
  )
}

function HazardTriage() {
  return (
    <TriageTable<HazardReport>
      title="Hazard reports"
      emptyTitle="No hazards reported"
      emptyBody="Hazards spotted by your team will appear here for triage."
      emptyIcon={<ShieldIcon size={22} />}
      endpoint="/leader/hazards"
      queryKey="hazards"
      columns={['Type', 'Risk', 'Reported', 'Description', 'Status']}
      rowCells={(r) => (
        <>
          <td>{titleCase(r.hazard_type)}</td>
          <td><StatusBadge status={r.risk_level} /></td>
          <td>{formatDate(r.occurred_at)}</td>
          <td style={{ maxWidth: 300 }}>{r.description || '—'}</td>
          <td><StatusBadge status={r.status} /></td>
        </>
      )}
    />
  )
}
